import { useState, useEffect } from "react";
import { compareModels } from "../../utils/fine-tuning-functions";
import CompareModelsGraphModal from "./CompareModelsGraphModal";
import EvaluationsModal from "./EvaluationsModal";

export default function CompareModels({ isOpen, onClose, userId }) {
  const [selectedModels, setSelectedModels] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [activeResultTab, setActiveResultTab] = useState(null);
  const [graphModalOpen, setGraphModalOpen] = useState(false);
  const [evaluationsModal, setEvaluationsModal] = useState(false);

  const availableModels = [
    { id: 1, name: 'gpt-4-turbo', provider: 'OpenAI' },
    { id: 2, name: 'gpt-4o', provider: 'OpenAI' },
    { id: 3, name: 'gpt-4o-mini', provider: 'OpenAI' },
    { id: 4, name: 'claude-3-sonnet-20240229', provider: 'Anthropic' },
    { id: 5, name: 'claude-3-haiku-20240307', provider: 'Anthropic' },
    { id: 6, name: 'claude-3-7-sonnet-20250219', provider: 'Anthropic' },
    { id: 7, name: 'gemini-1.5-pro', provider: 'Google' },
    { id: 8, name: 'gemini-1.5-flash', provider: 'Google' },
  ];

  const freePrompt = `You need to capture as much medical information as possible from the conversation or dictation. Absolutely do not hallucinate. Capture all relevant medical information in a concise way. Be objective. Do not infer sentences. Only use the information in the transcript to create the note. In the \"Chief Complaint\", be concise and short, for example \"Right knee pain\" or \"MRI review of the left shoulder\". If this is an MRI review or pre-op or post-op consultation, include this in the \"Chief Complaint\". Include occupation and physical activity in the \"History of Present Illness\" if mentioned. Do not include age of the patient unless mentioned. Include if the patient is a new patient or follow-up if this is able to be found in the transcript. Write \"History of Present Illness\" in paragraph form and include all patient symptoms, relevant past medical history, all medications, and any dates mentioned, especially date of injury or past surgeries. Make sentences concise and succinct in History of Present Illness. Add physical examination findings and imaging findings to the \"Objective\" section from the conversation in bullet points. Do not include subjective opinion or inferences in \"Physical Examination\". In \"Imaging\", put any imaging reviews in bullet points and include the date of the imaging if possible. If the imaging is discussed, include \"[imaging] was/were personally reviewed, interpreted, and demonstrated…\" Include if imaging is weight-bearing/non-weightbearing and three views, if found in transcript. In the \"Assessment/Plan\", separate diagnoses into a separate numbered list. Write the \"Plan\" in past tense in concise and succinct bullet points, for example \"Administered cortisone injection into the right knee\" or \"Provided a walking boot to help with ambulation\" or \"Reviewed treatment options\". Include all relevant discussion of diagnoses in the plan. Write the plan in first person; do not say \"the doctor\" or \"the physician\" or \"the provider.\" Use the provided starting note and fill out the rest of it. Use the provided starting note and fill out the rest of it. If the starting note already contains information in these portions, synthesize the information together into one coherent note and do not contradict information. If you are unable to determine from the transcript any information, output the original starting note. There are also some reference notes from the same doctor, focus on learning the language used, how each note is written, and formatting and structure of the reference notes. Reference these notes when writing the new note if it is helpful. Do not hallucinate. Output the SOAP note between [SOAP NOTE START] [SOAP NOTE END] [STARTING NOTE START]\n\nHistory of Present Illness\n--------------------------\nPhysical Exam\n—-------------\n\nImaging\n-------\n\nAssessment/Plan\n----------------\n\nFollow-up:\n\nPatient has given prior verbal consent to have the conversation recorded and summarized by the Knowtex software.\n[STARTING NOTE END]\n[TRANSCRIPT START]\n So this is Jacob. Jacob's here. A new patient visit for his right ankle. So Jacob, if you could tell me about how long it's been bothering you. And then the injury, describe the injury a little bit more to me. And then kind of what you've tried for so far. Okay, so it's been roughly 12 weeks. Okay. Which I know is probably not ideal, but it was right between jobs. In that period I didn't have health insurance, so I waited. Yeah. And was hoping it was a spring. So lots of ice. And what happened to it? What do you think? So I was playing with my puppy and I stepped off my front porch onto my sidewalk, but I stepped on the edge of the sidewalk. So my ankle went down and rolled out and it was a loud audible pop. And I immediately hit the ground. And the first two weeks were like brutal. Um, but I tried to elevate it ice on and off. Okay. Right. And though. Um, and then But now it's been that long and I randomly get pain in the front or the back or like around the bone on the outside. So I don't have the full range of motion either. Like so if I bend down to like play with the dog, like I can't bend that ankle up as far and it clicks and pops like it never has done before. So I figured I should probably have it checked. Well I probably better to get it checked out. No, good. I'm glad you came in. And how about swelling? Have you got swelling? Um, initially the, this all turned yellow and my foot and like a little bit of purple on the outside of the foot area. Okay. No, I got it. Got it. Uh, exercise wise, anything you'd like to do routinely? What do you like to do? No, not like I'm not a runner or anything. Gotcha. And work wise, are you on your feet? Um, so I work from home and I have a standing desk, so I do have one of those boards that like wobbles, but I can only stand on it for so long before my ankle starts to bother me. Okay. I'm looking at your medical problems, no diabetes, is that correct? Pretty healthy in general, looks like. Is that all? Yeah. You have that left knee, you've had ACL surgery. Gotcha. All right. All right. Can I examine you now? Yeah. Go ahead and stand for me if you would. I'll get you. I'll just grab it out loud for you and for our software. Okay. On standing inspection, there's neutral alignment at the knees, at the ankles and feet. There's some mild pronation, right and left, but some... symmetric, same on both sides. All right, you have a seat for me. And then seat examination, right lower leg, ankle and foot. There's some mild diffuse swelling about the ankle joint. Put this down for me. Just make sure everything's working for you. Can you bring this up for me? Good, able to dorsiflex the ankle, the tibialis anterior tendon's intact and functioning. Achilles tendon, there's no palpable defect. Good, push down for me. Good, plantar flexion strength, good. Now I'm going to come over here. Any tenderness along here? No, that seems to be okay. You know something if you need tenderness along the posterior lateral ankle, no tenderness along the perineal tendons. Can you push out against my hand if you would? Some mild weakness with eversion of the foot, no gross subluxation or dislocation of the perineal tendons. Get those things for you. And then, is it tender in here? Oh, that's okay, but like putting my shoe on and off like at a certain angle like it's a sharp pain Yeah, there's no no focal tenders today he points to the anterior ankle and the lateral ankle where it does get tender for him There's a small effusion At the ankle joint itself Everything's working. So that's good Good, alright Let's look at your x-rays looking at them a little bit before I came in but I always like to examine and look at them together again. I'm going to describe these out loud again. So we have ankle x-rays from today's visit, August 14th, 2023. Three views of the ankle weight-bearing which show no major old fractures. That's good. Symmetric ankle mortis. No apparent, no apparent displaced osteochondral defect. Overall and then no acute or stress fractures either. All right. So, a couple things I'm thinking about for you. So number one. That's what happened, inversion injured your ankle with a severe ankle sprain. That's how you describe it, that's severe. With a severe ankle sprain, you tear the ligaments. The good news is the vast majority of the time they'll heal, but sometimes you need help getting better. That's number one. Number two is you have some, still a little bit of swelling in the ankle joint itself. You might have been... cleaned up some cartilage in the ankle joint, alright, a little bit. So, you know, keeping that in mind, you know, at this point I don't see any definite evidence of it. Sometimes I'll see it, but I don't see it. But it's still possible that it's there. And then, the vast majority of the time, luckily, it kinda sorts itself out, you know, with some guidance. And then, those are kind of the main things I'm thinking about for you there, okay? In terms of treatment recommendations, okay, so we've got some recommendations how to quiet this down. So oftentimes, like if I want to get you like a sport ankle brace to kind of protect it a little bit, okay? The way I want to use this ankle brace is, oops, that's not the one I wanted, sorry. So what we want to do, you know, I'm stuck typing on the computer again, unfortunately, So use an ankle brace, you know, pretty consistently. Don't sleep or anything like that. But you're walking more than room to room, you put it on. Okay. You know, for about two weeks. And then I want you to then go to part-time, uneven terrain, anything more, kind of standing and walking, still use that until you see me back. Okay. In two weeks, I want you to start doing some exercises. All right. We're going to teach you that to work on strengthening. Again, those ligaments. heal but now if we kind of teach you some strengthening it'll heal better. Okay. Okay because a motion controlled motion and strengthening is good for those ligaments as they heal and I'd like you to try to do those exercises daily for two weeks and then every other day for two weeks we'll teach you how to do that okay. And that will help with range of motion as well. Yes exactly yep they'll come right but the motion I'm not so worried about is the is the inflammation that I see still there. It's still a little swollen in the joint so we're trying to kind of to deal with that first and then go to the other. Okay? And the other way to kind of get that swelling down is ice, okay, and this regimen here. So icing it later in the day, couple times a day for two or three days, and then daily for at least four to five days, maybe a couple weeks for you, okay, to get this swelling. Another thing I really like is called diclofenac, it's a topical anti-inflammatory medicine, over-the-counter inexpensive, you can put it right on the front of the ankle, absorbs right into there, I think that'd be nice. nice for you. Use that in the morning and the evening I'd suggest. And then, you know, I think it may be worth for you taking some Aleve. It's a half strength anti-inflammatory medicine. One in the morning, one at night. It's a 12 hour medicine. You just pop one here and there. It doesn't really do anything for this, but take a little more consistently for a week or two if it bothers your stomach. Stop. Okay. And then usually what I like to do is see you back, you know, probably in about four to six weeks, somewhere in there. And hopefully the swelling is coming down, you know. I want to make sure that swelling starts to come down and then oftentimes we start to increase things again. The standing desk and any of that wobbling part, you know, that avoid it. Yeah, that's probably not helping unfortunately at this point. Okay. For the last like two weeks I've been off of it. Yeah. It's starting to be annoying. Right, right. Exactly. So just because you said ligament and having gone through the ACL stuff. Yeah. This is something... that's gonna heal itself. This is different, right? Okay. Yeah, you know, the vast, you know, 95% of these injuries heal themselves. Okay. Hey, Mo, can we get him an ASO ankle brace and a medial resistance Thera-band, if you would? Sure. What type of shoe are you? 10, 10 1 1 1. Yeah, so the vast majority of these heal themselves. I was checking for, like, and I didn't see any evidence of your tendon dislocating. That's what I was checking for, right? So you can tore the ligaments, the vast majority of the times they'll heal. The problem is sometimes the heel elongate a little bit, then you get a little instability. But you know, you're not having that trouble at this point. Yeah, it's a weird thing. Like I said, on and off with my shoes sometimes, and swimming was not a great experience. Yeah, I agree with what you said. Yeah, exactly. Little things like that. Yeah, no. Or like, stomping a gas pedal. Definitely, like I notice it. That all makes sense, because again, I don't think you did yourself any long-term harm, but the fact that you had... a severe ankle sprain and you didn't happen to be able to treat it, is going to prolong your recovery, I feel. But I think a good chance it's going to come around. And at that follow-up visit in four to six weeks, no further x-rays are anticipated. Good.\n[TRANSCRIPT END]`;

  // Filter out models that are already selected
  const filteredModels = availableModels.filter(
    model => !selectedModels.some(selected => selected.id === model.id)
  );

  // Add a model to the selected list
  const handleAddModel = (model) => {
    setSelectedModels([...selectedModels, model]);
    setIsSelecting(false);
  };

  // Remove a model from the selected list
  const handleRemoveModel = (modelId) => {
    setSelectedModels(selectedModels.filter(model => model.id !== modelId));
  };

  // Reset the selection when the modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedModels([]);
      setPrompt("");
      setCompareResult(null);
      setIsLoading(false);
      setActiveResultTab(null);
    }
  }, [isOpen]);

  // Set initial active tab when results come in
  useEffect(() => {
    if (compareResult && !activeResultTab && Object.keys(compareResult).length > 0) {
      // Set the first model as the active tab
      const firstModelKey = Object.keys(compareResult).find(key => key !== 'comparison');
      if (firstModelKey) {
        setActiveResultTab(firstModelKey);
      }
    }
  }, [compareResult, activeResultTab]);

  // Handle comparison initiation
  const handleCompare = async () => {
    try {
      setIsLoading(true);

      const payload = {
        userId: userId,
        models: selectedModels.map(model => model.name),
        prompt: prompt
      };

      const result = await compareModels(payload);
      setCompareResult(result);
    } catch (error) {
      console.error("Error comparing models:", error);
      // Handle error state if needed
    } finally {
      setIsLoading(false);
    }
  };

  // Format a number to have only 2 decimal places
  const formatNumber = (num) => {
    return num ? parseFloat(num).toFixed(6) : '0.00';
  };

  // Get provider color
  const getProviderColor = (modelName) => {
    if (modelName.includes('gpt')) return 'bg-green-100 text-green-800 border-green-200';
    if (modelName.includes('claude')) return 'bg-purple-100 text-purple-800 border-purple-200';
    if (modelName.includes('gemini')) return 'bg-blue-100 text-blue-800 border-blue-200';
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg shadow-xl ${compareResult ? 'max-w-5xl' : 'max-w-2xl'} w-full max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">
            {compareResult ? "Model Comparison Results" : "Compare Models"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-4 flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-ktx-purple"></div>
              <p className="mt-4 text-lg font-medium text-gray-700">Comparing models...</p>
              <p className="mt-2 text-sm text-gray-500">This may take a minute as we process responses from each model.</p>
            </div>
          ) : compareResult ? (
            <div className="space-y-6">
              {/* Summary Section - Using only the data from the API */}
              {compareResult.comparison && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-lg text-ktx-purple mb-3">Comparison Summary</h4>
                    {compareResult && (
                      <div className="flex mb-3">
                        <button
                          type="button"
                          onClick={() => setGraphModalOpen(true)}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-ktx-purple bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          View Comparison Charts
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {compareResult.comparison.fastest_model && (
                      <div className="p-3 bg-white rounded-md border border-gray-200 shadow-sm">
                        <div className="text-sm text-gray-500 mb-1">Fastest Model</div>
                        <div className="font-medium text-gray-900">
                          {compareResult.comparison.fastest_model}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Latency: {(compareResult[compareResult.comparison.fastest_model]?.analytics.latency).toFixed(2)}s
                        </div>
                      </div>
                    )}

                    {compareResult.comparison.most_cost_effective && (
                      <div className="p-3 bg-white rounded-md border border-gray-200 shadow-sm">
                        <div className="text-sm text-gray-500 mb-1">Most Cost-Effective</div>
                        <div className="font-medium text-gray-900">
                          {compareResult.comparison.most_cost_effective}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Cost: ${formatNumber(compareResult[compareResult.comparison.most_cost_effective]?.analytics.cost)}
                        </div>
                      </div>
                    )}

                    {compareResult.comparison.most_verbose && (
                      <div className="p-3 bg-white rounded-md border border-gray-200 shadow-sm">
                        <div className="text-sm text-gray-500 mb-1">Most Verbose</div>
                        <div className="font-medium text-gray-900">
                          {compareResult.comparison.most_verbose}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          Tokens: {compareResult[compareResult.comparison.most_verbose]?.analytics.output_tokens || 0}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Model Response Tabs */}
              <div>
                <div className="border-b border-gray-200">
                  <nav className="flex overflow-x-auto">
                    {Object.keys(compareResult)
                      .filter(key => key !== 'comparison')
                      .map(modelName => (
                        <button
                          key={modelName}
                          onClick={() => setActiveResultTab(modelName)}
                          className={`py-2 px-4 text-sm font-medium border-b-2 whitespace-nowrap ${activeResultTab === modelName
                            ? 'border-ktx-purple text-ktx-purple'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                          {modelName}
                        </button>
                      ))}
                  </nav>
                </div>

                {/* Active Model Content */}
                {activeResultTab && compareResult[activeResultTab] && (
                  <div className="mt-4 space-y-4">
                    {/* Model Info */}
                    <div className="flex flex-wrap items-center justify-between mb-3">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getProviderColor(activeResultTab)}`}>
                          {activeResultTab.includes('gpt') ? 'OpenAI' :
                            activeResultTab.includes('claude') ? 'Anthropic' :
                              activeResultTab.includes('gemini') ? 'Google' : 'Other'}
                        </span>
                        <h3 className="ml-2 text-lg font-medium text-gray-900">{activeResultTab}</h3>
                      </div>

                      <div className="flex space-x-4 text-sm text-gray-500">
                        <div>
                          Latency: <span className="font-medium">{(compareResult[activeResultTab].analytics.latency).toFixed(2)}s</span>
                        </div>
                        <div>
                          Cost: <span className="font-medium">${formatNumber(compareResult[activeResultTab].analytics.cost)}</span>
                        </div>
                        <div>
                          Tokens: <span className="font-medium">{compareResult[activeResultTab].analytics.input_tokens + compareResult[activeResultTab].analytics.output_tokens}</span>
                        </div>

                        {compareResult[activeResultTab].analytics.evaluations && (
                          <button
                            onClick={() => setEvaluationsModal(true)}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-blue-50 text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            View Evaluations
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Response Content */}
                    <div className="border border-gray-200 rounded-md bg-gray-50 p-4 overflow-auto max-h-80">
                      <pre className="whitespace-pre-wrap text-sm text-gray-800 font-mono">
                        {compareResult[activeResultTab].response}
                      </pre>
                    </div>

                    {/* Analytics */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="bg-white p-3 rounded-md border border-gray-200">
                        <div className="text-sm font-medium text-gray-700 mb-1">Input Tokens</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {compareResult[activeResultTab].analytics.input_tokens}
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-md border border-gray-200">
                        <div className="text-sm font-medium text-gray-700 mb-1">Output Tokens</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {compareResult[activeResultTab].analytics.output_tokens}
                        </div>
                      </div>

                      <div className="bg-white p-3 rounded-md border border-gray-200">
                        <div className="text-sm font-medium text-gray-700 mb-1">Total Cost</div>
                        <div className="text-lg font-semibold text-gray-900">
                          ${formatNumber(compareResult[activeResultTab].analytics.cost)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            // Original Input Form
            <>
              <p className="text-gray-600 mb-4">
                Select models to compare their performance, features, and capabilities.
              </p>

              {/* Selected Models */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Selected Models ({selectedModels.length}/8)
                </label>

                {selectedModels.length > 0 ? (
                  <div className="space-y-2">
                    {selectedModels.map(model => (
                      <div
                        key={model.id}
                        className="flex items-center justify-between bg-gray-50 p-3 rounded-md border border-gray-200"
                      >
                        <div>
                          <span className="font-medium">{model.name}</span>
                          <span className="ml-2 text-xs text-gray-500">{model.provider}</span>
                        </div>
                        <button
                          onClick={() => handleRemoveModel(model.id)}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-md border border-dashed border-gray-300">
                    <p className="text-gray-500">No models selected yet. Add models to compare.</p>
                  </div>
                )}
              </div>

              {/* Model Selector */}
              <div className="relative mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Models to Compare
                </label>

                {isSelecting ? (
                  <div className="absolute z-10 mt-1 w-full bg-white rounded-md shadow-lg border border-gray-300 max-h-60 overflow-auto">
                    <ul className="py-1">
                      {filteredModels.length > 0 ? (
                        filteredModels.map(model => (
                          <li
                            key={model.id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center"
                            onClick={() => handleAddModel(model)}
                          >
                            <span>{model.name}</span>
                            <span className="text-xs text-gray-500">{model.provider}</span>
                          </li>
                        ))
                      ) : (
                        <li className="px-4 py-2 text-gray-500">All models have been selected</li>
                      )}
                    </ul>
                  </div>
                ) : null}

                <div
                  onClick={() => setIsSelecting(!isSelecting)}
                  className={`flex items-center justify-between p-2 border rounded-md cursor-pointer ${selectedModels.length >= 8 ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
                    }`}
                >
                  <span className="text-gray-700">
                    {selectedModels.length >= 8
                      ? "Maximum models selected (8)"
                      : "Select a model to add"}
                  </span>
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Prompt Input */}
              <div className="mb-6">
                <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
                  Test Prompt
                </label>
                <textarea
                  id="prompt"
                  rows="4"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="Enter a prompt to compare the models' responses..."
                  className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-ktx-purple focus:border-ktx-purple sm:text-sm"
                ></textarea>

                <div className="mt-2 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    The prompt will be sent to all selected models to evaluate their responses.
                  </p>

                  <button
                    type="button"
                    onClick={() => setPrompt(freePrompt)}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
                  >
                    <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Use Sample Medical Prompt
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          {compareResult ? (
            <>
              <button
                type="button"
                onClick={() => {
                  setCompareResult(null);
                  setActiveResultTab(null);
                }}
                className="mr-2 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
              >
                Start New Comparison
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-ktx-purple hover:bg-ktx-purple-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                className="mr-2 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCompare}
                disabled={selectedModels.length < 2 || !prompt.trim() || isLoading}
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple ${(selectedModels.length < 2 || !prompt.trim() || isLoading)
                  ? 'bg-ktx-purple-light cursor-not-allowed'
                  : 'bg-ktx-purple hover:bg-ktx-purple-dark'
                  }`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>Compare {selectedModels.length > 0 ? `(${selectedModels.length})` : ""}</>
                )}
              </button>
            </>
          )}
        </div>
      </div>
      <CompareModelsGraphModal
        isOpen={graphModalOpen}
        onClose={() => setGraphModalOpen(false)}
        compareResult={compareResult}
      />
      <EvaluationsModal
        isOpen={evaluationsModal}
        onClose={() => setEvaluationsModal(false)}
        evaluations={activeResultTab ? compareResult[activeResultTab]?.analytics.evaluations : null}
        modelName={activeResultTab}
      />
    </div>
  );
}